/**
 * @file Utilities that handle API calls. Whenever possible, functions should
 * return a Promise that resolves to the relevant data -- the rest of the app
 * should require no knowledge of Axios.
 */

import axios, { AxiosInstance } from 'axios';
import { Configuration, LogLevel } from "@azure/msal-browser";
import type { IActivationRequest, ICompany, ICustomer, ILicense } from './IDB';

// listen for expired session
(function (): void {
    axios.interceptors.response.use((response) => response, (error) => {
        if (error.response.status === 401) {
            window.location.assign('/');
        }
        return Promise.reject(error);
    });
}());

const hostSite = window.location.host === "licensing.cloud.anark.com"
    ? "https://licensing.cloud.anark.com"
    : "http://localhost:8000"

export const msalConfig: Configuration = {
    auth: {
        clientId: "f1d8939d-9648-4c58-b2ac-80799398cdb5",
        authority: "https://login.microsoftonline.com/f3dd6f0d-b0d8-4caa-8b23-d3d628a171d5",
        redirectUri: hostSite,
        postLogoutRedirectUri: hostSite,
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
    },
    system: { loggerOptions: { logLevel: LogLevel.Info } }
};

export const apiScope = [`api://66c6dd75-7cab-4a6a-865d-306ced80d385/user_impersonation`];

export class Server {
    public backend: AxiosInstance;
    public static instance: Server;

    constructor(accessToken: string) {
        this.backend = axios.create({
            baseURL: hostSite,
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${accessToken}`,
            }
        });
        axios.interceptors.response.use((response) => response, (error) => {
            if (error && error.response && error.response.status === 401) {
                window.location.reload()
            }
            return Promise.reject(error);
        });
        this.getCustomers = this.getCustomers.bind(this);
        this.createCustomer = this.createCustomer.bind(this);
        this.updateCustomer = this.updateCustomer.bind(this);
        this.createLicense = this.createLicense.bind(this);
    }

    public static getInstance(accessToken?: string): Server {
        if (accessToken) {
            Server.instance = new Server(accessToken);
        }
        return Server.instance;
    }

    public async getCustomers(limit?: number | null, page?: number | null, search?: string): Promise<ICustomer[]> {
        let optionalQuery = [];
        if (limit) {
            optionalQuery.push('limit=' + limit);
        }
        
        if (page) {
            optionalQuery.push('page=' + page);
        }

        if (search) {
            optionalQuery.push('search=' + search);
        }
        
        const getResponse = await this.backend.get(`/api/customers${optionalQuery.length > 0 ? '?' + optionalQuery.join('&') : ''}`);
        const data = getResponse.data.map((item: ICustomer) => {
            return {
                ...item,
                licenses: item.licenses.map((lic) => {
                    return {
                        ...lic,
                        licenseType: (lic.licenseType as string) === "New Commercial License" ? "NEW"
                            : (lic.licenseType as string) === "Evaluation License" ? "EVAL"
                                : (lic.licenseType as string) === "Anark Internal Use" ? "ANARK" : lic.licenseType
                    }
                })
            }
        });
        return data;
    };


    public async getCustomer(customerId: string): Promise<ICustomer> {
        const getResponse = await this.backend.get(`/api/customers/id/${customerId}`);
        const data = getResponse.data as ICustomer;
        return {
            ...data,
            licenses: data.licenses.map((lic: ILicense) => {
                return {
                    ...lic,
                    licenseType: (lic.licenseType as string) === "New Commercial License" ? "NEW"
                        : (lic.licenseType as string) === "Evaluation License" ? "EVAL"
                            : (lic.licenseType as string) === "Anark Internal Use" ? "ANARK" : lic.licenseType
                }
            })
        }
    };

    public async createCustomer(userForm: {
        name: string;
        email: string;
        company: number;
        phone: string;
        address: string;
    }): Promise<ICustomer> {
        const postResponse = await this.backend.post('/api/customers/create', userForm);
        return postResponse.data;
    }

    public async createCompany(name: string): Promise<number> {
        const postResponse = await this.backend.post('/api/customers/company', { name });
        return postResponse.data.id;
    }

    public async updateCustomer(customerId: number, customerForm: {
        name: string;
        email: string;
        company: number;
        phone: string;
        address: string;
    }): Promise<void> {
        await this.backend.put(`/api/customers/${customerId}`, customerForm);
    }

    public async getCompanies(): Promise<ICompany[]> {
        const getResponse = await this.backend.get('/api/customers/companies');
        return getResponse.data;
    }

    public async createLicense(license: Omit<ILicense, "used" | "code" | "created" | "revoked" | "history">): Promise<ILicense> {
        const postResponse = await this.backend.post('/api/licenses/create', license);
        return postResponse.data;
    };

    public async activateProduct(data: IActivationRequest): Promise<string> {
        const postResponse = await this.backend.post('/api/licenses/activate', data);
        return postResponse.data.LicenseFile;
    }

    public async revokeLicense(licenseCode: string): Promise<void> {
        await this.backend.put(`/api/licenses/${licenseCode}/revoke`);
    }

    public async downloadLicenseFiles(licenseCode: string): Promise<void> {
        window.open(`/api/licenses/${licenseCode}/download`);
    }

    public async viewLicense(licenseCode: string): Promise<string> {
        const getResponse = await this.backend.get(`/api/licenses/${licenseCode}`);
        return getResponse.data.LicenseFile;
    }

    public async renewLicense(licenseCode: string, invoice: string, expiration: string, hostName?: string): Promise<string> {
        const postResponse = await this.backend.post('/api/licenses/renew', {
            activationCode: licenseCode,
            invoice: invoice,
            expiry: expiration,
            hostName: hostName
        });
        return postResponse.data?.LicenseFile;
    }

    public async transferLicense(licenseCode: string, hostName: string): Promise<string> {
        const postResponse = await this.backend.post('/api/licenses/transfer', {
            activationCode: licenseCode,
            hostName: hostName
        });
        return postResponse.data?.LicenseFile;
    }

    public async deleteCustomer(customerId: number): Promise<void> {
        await this.backend.delete(`/api/customers/${customerId}`);
    }
}